/**
 * Shared paste operations hook for track and playlist pasting
 */

import { useState } from 'react';
import { toast } from '../components/ui/Toast';
import { useWorkbenchStore } from '../stores/workbench';
import { useAddTracks } from '../api/hooks/usePlaylists';
import { parseTrackFromClipboard, parsePlaylistFromClipboard } from '../utils/spotify-import';
import { playlistService } from '../api/services/playlists';

export function usePasteOperations() {
  const { activePlaylist } = useWorkbenchStore();
  const addTracksMutation = useAddTracks();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContext, setModalContext] = useState<{
    type: 'track' | 'playlist';
    position?: number;
  }>({ type: 'track' });

  /**
   * Paste a single track from clipboard
   * @param position - Position to insert at (0-indexed, optional - defaults to end)
   */
  const pasteSong = async (position?: number) => {
    if (!activePlaylist) {
      toast.error('No active playlist');
      return;
    }

    try {
      const clipboardText = await navigator.clipboard.readText();
      const trackId = parseTrackFromClipboard(clipboardText);

      if (!trackId) {
        toast.warning('No valid Spotify track URL found in clipboard.\n\nExpected format:\nhttps://open.spotify.com/track/abc123...');
        return;
      }

      const trackUri = `spotify:track:${trackId}`;

      // Add track to Spotify at the correct position
      await addTracksMutation.mutateAsync({
        playlistId: activePlaylist.id,
        data: {
          uris: [trackUri],
          position: position, // If undefined, adds to end
        },
      });

      // The mutation's onSuccess will invalidate queries and trigger workbench refresh
    } catch (error) {
      console.error('Failed to paste song:', error);

      // Check for clipboard permission error
      if (error instanceof DOMException && error.name === 'NotAllowedError') {
        // Open modal for manual paste
        setModalContext({ type: 'track', position });
        setIsModalOpen(true);
      } else {
        toast.error('Failed to paste song. Please try again.');
      }
    }
  };

  /**
   * Paste entire playlist from clipboard
   * @param position - Position to insert at (0-indexed, optional - defaults to end)
   */
  const pastePlaylist = async (position?: number) => {
    if (!activePlaylist) {
      toast.error('No active playlist');
      return;
    }

    try {
      const clipboardText = await navigator.clipboard.readText();
      const playlistId = parsePlaylistFromClipboard(clipboardText);

      if (!playlistId) {
        toast.warning('No valid Spotify playlist URL found in clipboard.\n\nExpected format:\nhttps://open.spotify.com/playlist/abc123...');
        return;
      }

      // Fetch source playlist
      const sourcePlaylist = await playlistService.get(playlistId);

      if (!sourcePlaylist) {
        toast.error('Could not load playlist. Please try again.');
        return;
      }

      // Extract track URIs
      const trackUris = sourcePlaylist.tracks.items.map(pt => pt.track.uri);

      if (trackUris.length === 0) {
        toast.warning('Playlist is empty');
        return;
      }

      // Add tracks to Spotify at the correct position
      await addTracksMutation.mutateAsync({
        playlistId: activePlaylist.id,
        data: {
          uris: trackUris,
          position: position, // If undefined, adds to end
        },
      });

      // The mutation's onSuccess will invalidate queries and trigger workbench refresh
    } catch (error) {
      console.error('Failed to paste playlist:', error);

      // Check for clipboard permission error
      if (error instanceof DOMException && error.name === 'NotAllowedError') {
        // Open modal for manual paste
        setModalContext({ type: 'playlist', position });
        setIsModalOpen(true);
      } else {
        toast.error('Failed to paste playlist. Please try again.');
      }
    }
  };

  /**
   * Handle manual paste from modal
   * Tries both track and playlist parsers, auto-detects type
   * @param url - Manually pasted URL
   */
  const handleManualPaste = async (url: string) => {
    if (!activePlaylist) {
      toast.error('No active playlist');
      setIsModalOpen(false);
      return;
    }

    try {
      // Try both parsers - auto-detect track vs playlist
      const trackId = parseTrackFromClipboard(url);
      const playlistId = parsePlaylistFromClipboard(url);

      if (trackId) {
        // Handle track paste
        const trackUri = `spotify:track:${trackId}`;
        await addTracksMutation.mutateAsync({
          playlistId: activePlaylist.id,
          data: { uris: [trackUri], position: modalContext.position },
        });
        toast.success('Track added successfully');
      } else if (playlistId) {
        // Handle playlist paste
        const sourcePlaylist = await playlistService.get(playlistId);
        if (!sourcePlaylist) {
          toast.error('Could not load playlist');
          return;
        }

        const trackUris = sourcePlaylist.tracks.items.map(pt => pt.track.uri);
        if (trackUris.length === 0) {
          toast.warning('Playlist is empty');
          setIsModalOpen(false);
          return;
        }

        await addTracksMutation.mutateAsync({
          playlistId: activePlaylist.id,
          data: { uris: trackUris, position: modalContext.position },
        });
        toast.success(`Added ${trackUris.length} tracks from playlist`);
      } else {
        toast.error('Invalid Spotify URL');
        return;
      }

      setIsModalOpen(false);
    } catch (error) {
      console.error('Failed to paste from manual input:', error);
      toast.error('Failed to add tracks. Please try again.');
    }
  };

  return {
    pasteSong,
    pastePlaylist,
    canPaste: !!activePlaylist,
    // Modal state for manual paste fallback
    isModalOpen,
    closeModal: () => setIsModalOpen(false),
    handleManualPaste,
  };
}
